import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { CSVLink } from "react-csv";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/theme";
import {
  getBranchTypeLabel,
  getEvaluationRoundLabel,
  type BranchType,
  type EvaluationRound,
  EVALUATION_ROUNDS,
} from "@/types/enums";
import { ScoreFilterCriteria } from "@/components/candidates/filters/ScoreFilter";
import {
  ASSESSMENT_ROUND_FILTER_VALUE,
  ASSESSMENT_ROUND_FILTER_ROUNDS,
} from "@/constants/candidateRoundFilters";

type EvaluationRoundData = {
  round_name: string;
  score: number | null;
};

type Candidate = {
  id: string;
  full_name: string;
  email: string;
  phone?: string | null;
  status: string;
  current_round?: string | null;
  final_score?: number | null;
  cgpa?: number | null;
  branch?: string | null;
  current_city?: string | null;
  permanent_city?: string | null;
  created_at: string;
  updated_at: string;
  colleges?: { name: string } | null;
  job_positions?: { role_name: string } | null;
  evaluation_rounds?: EvaluationRoundData[];
};

type FilterOption = {
  id: string;
  name?: string;
  role_name?: string;
};

interface CandidateExportManagerProps {
  searchTerm: string;
  statusFilter: string;
  roundFilter: string;
  collegeFilter: string;
  positionFilter: string;
  cityFilter: string;
  sortBy: string;
  sortOrder: string;
  scoreFilters: ScoreFilterCriteria[];
  colleges: FilterOption[];
  jobPositions: FilterOption[];
  disabled?: boolean;
  selectedCandidateIds?: Set<string>;
}

const getImageDataUrl = (src: string) =>
  new Promise<{ dataUrl: string; width: number; height: number }>(
    (resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to prepare logo for PDF export"));
        return;
      }

      context.drawImage(image, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };

    image.onerror = () => {
      reject(new Error("Unable to load logo for PDF export"));
    };

      image.src = src;
    }
  );

export const CandidateExportManager = React.memo(
  ({
    searchTerm,
    statusFilter,
    roundFilter,
    collegeFilter,
    positionFilter,
    cityFilter,
    sortBy,
    sortOrder,
    scoreFilters,
    colleges,
    jobPositions,
    disabled = false,
    selectedCandidateIds = new Set(),
  }: CandidateExportManagerProps) => {
    const { toast } = useToast();
    const { theme } = useTheme();
    const [csvData, setCsvData] = useState<any[]>([]);
    const [isExporting, setIsExporting] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const csvLinkRef = useRef<
      CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
    >(null);

    const getBranchLabel = (branch?: string | null) => {
      if (!branch) return "N/A";
      return getBranchTypeLabel(branch as BranchType);
    };

    const getReportTitle = () => {
      if (collegeFilter !== "all") {
        return (
          colleges.find((college) => college.id === collegeFilter)?.name ||
          collegeFilter
        );
      }

      return "Candidates";
    };

    const fetchAllCandidatesForExport = async (): Promise<Candidate[]> => {
      try {
        let query = supabase.from("candidates").select(`
        *,
        colleges:college_id(name),
        job_positions:job_position_id(role_name),
        evaluation_rounds!left(round_name, score)
      `);

        // Apply server-side filters
        if (statusFilter !== "all") {
          query = query.eq("status", statusFilter);
        }
        if (roundFilter === ASSESSMENT_ROUND_FILTER_VALUE) {
          query = query.in("current_round", [...ASSESSMENT_ROUND_FILTER_ROUNDS]);
        } else if (roundFilter !== "all") {
          query = query.eq("current_round", roundFilter);
        }
        if (collegeFilter !== "all") {
          query = query.eq("college_id", collegeFilter);
        }
        if (positionFilter !== "all") {
          query = query.eq("job_position_id", positionFilter);
        }
        if (cityFilter !== "all") {
          query = query.eq("current_city", cityFilter);
        }

        // Apply search filter
        if (searchTerm) {
          query = query.or(
            `full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
          );
        }

        // Apply score filters server-side where possible
        const cgpaFilters = scoreFilters.filter(
          (f) => f.round === "cgpa"
        );
        const finalScoreFilters = scoreFilters.filter(
          (f) => f.round === "final_score"
        );
        const roundScoreFilters = scoreFilters.filter(
          (f) =>
            f.round !== "cgpa" &&
            f.round !== "final_score" &&
            f.round !== "assessment_round"
        );

        cgpaFilters.forEach((f) => {
          query = query.gte("cgpa", f.minScore);
          if (f.maxScore) {
            query = query.lte("cgpa", f.maxScore);
          }
        });

        finalScoreFilters.forEach((f) => {
          query = query.gte("final_score", f.minScore);
          if (f.maxScore) {
            query = query.lte("final_score", f.maxScore);
          }
        });

        if (roundScoreFilters.length > 0) {
          const orParts = roundScoreFilters.map((f) => {
            const maxPart = f.maxScore ? `,score.lte.${f.maxScore}` : "";
            return `and(round_name.eq.${f.round},score.gte.${f.minScore}${maxPart})`;
          });
          query = query.or(orParts.join(","), { foreignTable: "evaluation_rounds" });
        }

        // Apply sorting
        query = query.order(sortBy, { ascending: sortOrder === "asc" });

        const { data, error } = await query;

        if (error) {
          console.error("Export query error:", error);
          throw error;
        }

        let allCandidates = (data as Candidate[]) || [];

        // Apply client-side score filters
        if (scoreFilters.length > 0) {
          allCandidates = allCandidates.filter((candidate) => {
            return scoreFilters.every((filter) => {
              if (filter.round === "cgpa") {
                const cgpa = candidate.cgpa || 0;
                return (
                  cgpa >= filter.minScore &&
                  (filter.maxScore ? cgpa <= filter.maxScore : true)
                );
              }
              if (filter.round === "final_score") {
                const finalScore = candidate.final_score || 0;
                return (
                  finalScore >= filter.minScore &&
                  (filter.maxScore ? finalScore <= filter.maxScore : true)
                );
              }
              if (filter.round === "assessment_round") {
                const mcq =
                  candidate.evaluation_rounds?.find(
                    (er) => er.round_name === EVALUATION_ROUNDS.MCQ
                  )?.score || 0;
                const open =
                  candidate.evaluation_rounds?.find(
                    (er) => er.round_name === EVALUATION_ROUNDS.OPEN_ENDED
                  )?.score || 0;
                const avg = (mcq + open) / 2;
                return (
                  avg >= filter.minScore &&
                  (filter.maxScore ? avg <= filter.maxScore : true)
                );
              }

              // Check evaluation rounds
              const roundScore =
                candidate.evaluation_rounds?.find(
                  (er) => er.round_name === filter.round
                )?.score || 0;
              return (
                roundScore >= filter.minScore &&
                (filter.maxScore ? roundScore <= filter.maxScore : true)
              );
            });
          });
        }

        return allCandidates;
      } catch (error) {
        console.error("Error fetching candidates for export:", error);
        throw error;
      }
    };

    const prepareCsvData = (candidatesToExport: Candidate[]) => {
      return candidatesToExport.map((candidate) => ({
        id: candidate.id,
        full_name: candidate.full_name,
        email: candidate.email,
        phone: candidate.phone || "N/A",
        college_name: candidate.colleges?.name || "N/A",
        branch: getBranchLabel(candidate.branch),
        cgpa: candidate.cgpa || "N/A",
        job_position_name: candidate.job_positions?.role_name || "N/A",
        current_round_label: candidate.current_round
          ? getEvaluationRoundLabel(candidate.current_round as EvaluationRound)
          : "Not Started",
        current_city: candidate.current_city || "N/A",
        permanent_city: candidate.permanent_city || "N/A",
        created_at: candidate.created_at,
        updated_at: candidate.updated_at,
      }));
    };

    const handleExportCsv = async () => {
      // Validate if candidates are selected
      if (selectedCandidateIds.size === 0) {
        toast({
          title: "No Candidates Selected",
          description: "Please select at least one candidate to export.",
          variant: "destructive",
        });
        return;
      }

      setIsExporting(true);
      try {
        const allCandidates = await fetchAllCandidatesForExport();
        // Filter to only selected candidates
        const selectedCandidates = allCandidates.filter((candidate) =>
          selectedCandidateIds.has(candidate.id)
        );
        const preparedData = prepareCsvData(selectedCandidates);
        setCsvData(preparedData);

        toast({
          title: "CSV Export Ready",
          description: `Preparing export for ${preparedData.length} candidate${preparedData.length !== 1 ? "s" : ""}`,
        });
      } catch (error) {
        console.error("Error exporting CSV:", error);
        toast({
          title: "CSV Export Failed",
          description:
            "There was an error exporting the CSV. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsExporting(false);
      }
    };

    const handleExportPdf = async () => {
      // Validate if candidates are selected
      if (selectedCandidateIds.size === 0) {
        toast({
          title: "No Candidates Selected",
          description: "Please select at least one candidate to export.",
          variant: "destructive",
        });
        return;
      }

      setIsExportingPdf(true);
      try {
        const allCandidates = await fetchAllCandidatesForExport();
        // Filter to only selected candidates
        const candidatesToExport = allCandidates.filter((candidate) =>
          selectedCandidateIds.has(candidate.id)
        );
        const logo = await getImageDataUrl(theme.logo);

        const doc = new jsPDF({ orientation: "landscape" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const tableColumnWidths = [32, 52, 26, 44, 24, 40, 28];
        const tableWidth = tableColumnWidths.reduce((sum, width) => sum + width, 0);
        const tableMarginX = Math.max((pageWidth - tableWidth) / 2, 14);
        const reportTitle = getReportTitle();
        const logoWidth = 34;
        const logoHeight = (logo.height / logo.width) * logoWidth;
        doc.addImage(logo.dataUrl, "PNG", 14, 10, logoWidth, logoHeight);

        doc.setTextColor(17, 24, 39);
        doc.setFontSize(10);
        doc.text(`College: ${reportTitle}`, 14, 34);
        doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 14, 40);
        doc.text(
          `Candidate Count: ${candidatesToExport.length}`,
          pageWidth - 14,
          40,
          { align: "right" }
        );

        const dividerY = 48;
        doc.setDrawColor(226, 232, 240);
        doc.line(14, dividerY, pageWidth - 14, dividerY);

        // Define table columns
        const tableColumn = [
          "Full Name",
          "Email",
          "Phone",
          "College",
          "Branch",
          "Position",
          "Permanent City",
        ];
        const tableRows: any[][] = [];

        candidatesToExport.forEach((candidate) => {
          const candidateData = [
            candidate.full_name,
            candidate.email,
            candidate.phone || "N/A",
            candidate.colleges?.name || "N/A",
            getBranchLabel(candidate.branch),
            candidate.job_positions?.role_name || "N/A",
            candidate.permanent_city || "N/A",
          ];
          tableRows.push(candidateData);
        });

        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: dividerY + 6,
          theme: "grid",
          margin: { left: tableMarginX, right: tableMarginX },
          tableWidth,
          styles: {
            fontSize: 8,
            cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
            overflow: "linebreak",
            valign: "middle",
            lineColor: [226, 232, 240],
          },
          headStyles: {
            fillColor: [22, 160, 133],
            textColor: 255,
            fontStyle: "bold",
            halign: "left",
          },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: tableColumnWidths[0] },
            1: { cellWidth: tableColumnWidths[1] },
            2: { cellWidth: tableColumnWidths[2] },
            3: { cellWidth: tableColumnWidths[3] },
            4: { cellWidth: tableColumnWidths[4] },
            5: { cellWidth: tableColumnWidths[5] },
            6: { cellWidth: tableColumnWidths[6] },
          },
        });

        doc.save("candidates_report.pdf");

        toast({
          title: "PDF Export Complete",
          description: `PDF exported with ${candidatesToExport.length} candidate${candidatesToExport.length !== 1 ? "s" : ""}`,
        });
      } catch (error) {
        console.error("Error exporting PDF:", error);
        toast({
          title: "PDF Export Failed",
          description:
            "There was an error exporting the PDF. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsExportingPdf(false);
      }
    };

    useEffect(() => {
      if (csvData.length > 0 && csvLinkRef.current) {
        // Small delay to ensure the CSV data is properly set
        setTimeout(() => {
          if (csvLinkRef.current && csvLinkRef.current.link) {
            csvLinkRef.current.link.click();
          }
          setCsvData([]);
        }, 100);
      }
    }, [csvData]);

    const csvHeaders = [
      { label: "ID", key: "id" },
      { label: "Full Name", key: "full_name" },
      { label: "Email", key: "email" },
      { label: "Phone", key: "phone" },
      { label: "College", key: "college_name" },
      { label: "Branch", key: "branch" },
      { label: "CGPA", key: "cgpa" },
      { label: "Job Position", key: "job_position_name" },
      { label: "Current Round", key: "current_round_label" },
      { label: "Final Score", key: "final_score" },
      { label: "Current City", key: "current_city" },
      { label: "Permanent City", key: "permanent_city" },
      { label: "Created At", key: "created_at" },
      { label: "Updated At", key: "updated_at" },
    ];

    return (
      <div className="flex space-x-2">
        <Button
          onClick={handleExportCsv}
          disabled={isExporting || isExportingPdf || disabled}
        >
          {isExporting ? (
            "Exporting CSV..."
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" /> Export to CSV
            </>
          )}
        </Button>
        <Button
          onClick={handleExportPdf}
          disabled={isExportingPdf || isExporting || disabled}
        >
          {isExportingPdf ? (
            "Exporting PDF..."
          ) : (
            <>
              <FileText className="h-4 w-4 mr-2" /> Download as PDF
            </>
          )}
        </Button>

        {csvData.length > 0 && (
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`candidates_export_${
              new Date().toISOString().split("T")[0]
            }.csv`}
            className="hidden"
            ref={csvLinkRef}
            target="_blank"
          />
        )}
      </div>
    );
  }
);

CandidateExportManager.displayName = "CandidateExportManager";
